import { computed, effect, Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { PublicLinkService } from '@simOn/public-link';
import { filter, map, startWith } from 'rxjs';

/**
 * Open / closed state of the AI assistant chat panel, shared between the launcher in the top navbar
 * (`@simOn/layout`) and the panel itself (`feature-ai-agent`). The assistant is scoped to a space, so
 * `available` is `true` only under `/space/:apartmentId` outside of public link mode (no authenticated
 * user there); leaving the space closes the panel.
 */
@Injectable({ providedIn: 'root' })
export class AiAssistantPanelService {
  private readonly _router = inject(Router);
  private readonly _publicLink = inject(PublicLinkService);
  private readonly _open = signal(false);

  readonly open = this._open.asReadonly();
  private readonly _inSpace = toSignal(
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this._router.url),
      map((url) => url.startsWith('/space/'))
    ),
    { initialValue: false }
  );
  private readonly _publicLinkMode = toSignal(this._publicLink.isPublicLinkModeActive$, { initialValue: false });
  readonly available = computed(() => this._inSpace() && !this._publicLinkMode());
  /** The panel is rendered only while it is open and the assistant is available on the current page. */
  readonly visible = computed(() => this.available() && this._open());

  constructor() {
    effect(() => {
      if (!this.available()) this._open.set(false);
    });
  }

  toggle(open = !this._open()): void {
    this._open.set(open);
  }

  close(): void {
    this._open.set(false);
  }
}
